import { PinIcon } from '@components/Icons/PinIcon';
import { ActionDrawerItem } from '@components/MobileActionDrawer/MobileActionDrawer';
import { verbMenuItem, type LibraryVerbId } from '@lib/verbs';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

/**
 * `LibraryVerbId` is derived from a tuple in `@lib/verbs/ids`, so renaming a verb breaks there
 * and propagates here. The housekeeping members are local — they aren't verbs and appear
 * nowhere else.
 */
export type LibraryMenuAction = LibraryVerbId | 'rename' | 'delete' | 'pin';

/**
 * Single source of truth for the library kebab (desktop) and context drawer
 * (mobile) — see `.claude/rules/context-menu-actions.md`.
 *
 * The two verbs lead, the housekeeping follows. The menu is where these verbs
 * keep their words: every icon-only `VerbRail` in the app is an abbreviation of
 * this list.
 */
export const LIBRARY_MENU_ACTIONS: (ActionDrawerItem & { id: LibraryMenuAction })[] = [
  // Labels and glyphs come from `@lib/verbs` — including the "one glyph per capability,
  // echoing something already labelled in the nav" rule that used to be explained here.
  // The order is still this file's business: verbs lead, housekeeping follows.
  verbMenuItem('work-with-agent'),
  verbMenuItem('find-moments'),
  { id: 'rename', label: 'Rename or edit', icon: <EditIcon fontSize="small" /> },
  { id: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, danger: true },
];

/**
 * The pin entry, kept out of `LIBRARY_MENU_ACTIONS` because its label and glyph depend on
 * the library's current pin state — which a static array can't express. Still lives in
 * this file so the wording stays in one place, per
 * `.claude/rules/context-menu-actions.md`.
 *
 * Only the mobile drawer composes it in: desktop reaches pinning through the card's hover
 * button and the table's pin column, so its kebab leaves this out.
 */
export const getLibraryPinAction = (
  isPinned: boolean,
): ActionDrawerItem & { id: LibraryMenuAction } => ({
  id: 'pin',
  // Just the verb — "to the top" would claim a position change that doesn't happen when
  // "Pinned first" is off. Same wording as `PinButton`'s tooltip.
  label: isPinned ? 'Unpin' : 'Pin',
  // 18px, not PinIcon's 12px default: the drawer's icon slot is 24px and the siblings are
  // MUI `fontSize="small"` (20px) glyphs, which carry internal padding this one doesn't.
  icon: <PinIcon width={18} height={18} filled={isPinned} />,
});
